<template>
  <div class="t-space-y-8">
    <section>
      <h2
        class="t-text-left t-text-2xl t-font-semibold t-mb-4 t-ml-4 md:t-ml-0"
      >
        Freight Offers
      </h2>

      <div
        v-if="loadingOffers"
        class="t-py-4 t-text-center t-text-2xl t-font-medium t-text-gray-900"
      >
        <i class="fas fa-x fa-spinner-third fa-spin"></i>
        Loading
      </div>

      <freight-offer-card
        v-for="offer of offers"
        :key="offer.id"
        :offer="offer"
        @acceptOfferClicked="onAcceptOfferClicked"
        @declineOfferClicked="onDeclineOfferClicked"
        class="t-mb-6"
      />

      <div
        v-if="!loadingOffers && offers.length === 0"
        class="t-flex t-items-center t-justify-center t-h-16 t-text-center t-text-xl t-font-medium t-text-gray-900"
      >
        You don't have any current offers
      </div>
    </section>

    <section>
      <h2
        class="t-text-left t-text-2xl t-font-semibold t-mb-4 t-ml-4 md:t-ml-0"
      >
        Offers History
      </h2>

      <div
        v-if="loadingPastOffers"
        class="t-py-4 t-text-center t-text-2xl t-font-medium t-text-gray-900"
      >
        <i class="fas fa-x fa-spinner-third fa-spin"></i>
        Loading
      </div>

      <div
        v-if="!loadingPastOffers && pastOffers.length > 0"
        class="t-overflow-x-auto t-mb-4"
      >
        <div class="t-align-middle t-inline-block t-min-w-full">
          <div>
            <div class="t-flex t-px-3 t-justify-between t-mb-2 t-font-semibold">
              <div class="t-w-28 t-flex t-flex-shrink-0 t-text-center">
                Status
              </div>
              <p class="t-w-20 t-px-2">ID</p>
              <p class="t-w-40 t-flex t-flex-grow t-px-3">Pickup</p>
              <p class="t-w-40 t-flex t-flex-grow t-px-3">Delivery</p>
              <p class="t-w-36 t-px-3">Freight</p>
              <p class="t-w-28 t-px-2">Price</p>
            </div>

            <div
              v-for="pastOffer in pastOffers"
              :key="pastOffer.id"
              class="t-flex t-p-3 t-justify-between t-mb-4 t-bg-white t-rounded-lg t-bg-white t-border t-border-gray-100 t-shadow-md"
            >
              <div class="t-w-28 t-flex t-items-center t-pr-4">
                <div
                  class="t-w-full t-flex t-text-center t-bg-gray-200 t-rounded-md t-py-1 t-px-3 t-text-sm t-my-auto t-font-medium"
                  :class="[
                    {
                      't-bg-green-200 t-text-green-900':
                        pastOffer.status === 'accepted',
                      't-bg-red-200 t-text-red-900':
                        pastOffer.status === 'declined',
                      't-bg-yellow-200 t-text-yellow-900':
                        pastOffer.status === 'withdrawn'
                    }
                  ]"
                >
                  <p class="t-capitalize t-mx-auto">
                    {{ pastOffer.status }}
                  </p>
                </div>
              </div>
              <div class="t-w-20 t-px-2">
                {{ pastOffer.id }}
              </div>
              <div class="t-w-40 t-flex t-flex-grow t-px-3">
                <p>{{ pastOffer.pickup_location.name }}</p>
              </div>
              <div class="t-w-40 t-flex t-flex-grow t-px-3">
                {{ pastOffer.delivery_location.name }}
              </div>
              <div class="t-w-36 t-px-3">
                <p>
                  {{ Math.trunc(parseFloat(pastOffer.weight)) }}Kgs,{{
                    parseFloat(pastOffer.volume)
                  }}m<sup>3</sup>
                </p>
              </div>
              <div class="t-w-28 t-px-2">${{ pastOffer.cost }}</div>
            </div>
          </div>
        </div>
      </div>

      <paginate
        v-if="!loadingPastOffers && pastOffers.length > 0"
        v-model="pagination.page"
        :page-count="pagination.last_page"
        :page-range="5"
        :click-handler="fetchPastOffers"
        :prev-text="'&laquo;'"
        :next-text="'&raquo;'"
        :prev-link-class="'page-link'"
        :next-link-class="'page-link'"
        :container-class="'pagination'"
        :page-class="'page-item'"
        :page-link-class="'page-link'"
        class="t-flex t-justify-end"
      />

      <div
        v-if="!loadingPastOffers && pastOffers && pastOffers.length === 0"
        class="t-flex t-items-center t-justify-center t-h-16 t-text-center t-text-xl t-font-medium"
      >
        No past offers yet
      </div>
    </section>

    <confirm-modal
      v-if="showAcceptConfirmModal"
      :title="'Accept Offer'"
      :text="'Confirm Acceptance'"
      :confirming="processingOffer"
      @cancelled="acceptModalCancelledHandler"
      @confirmed="acceptModalConfirmedHandler"
    />

    <confirm-modal
      v-if="showDeclineConfirmModal"
      :title="'Decline Offer?'"
      :text="'Are you sure that you want to decline this offer?'"
      :confirming="processingOffer"
      @cancelled="declineModalCancelledHandler"
      @confirmed="declineModalConfirmedHandler"
    />
  </div>
</template>

<script>
import FreightOfferCard from "../cards/FreightOfferCard";
import { LOAD_OFFERS } from "../../../../modules/offers";
import ConfirmModal from "../../../utils/ConfirmModal";
import FixedRateItem from "../../fixed-rates/FixedRateItem";
import Paginate from "vuejs-paginate";

export default {
  name: "FreightOffersTab",
  components: {
    FreightOfferCard,
    FixedRateItem,
    Paginate
  },
  props: {
    ConfirmModal
  },
  data() {
    return {
      processingOffer: false,
      showAcceptConfirmModal: false,
      showDeclineConfirmModal: false,
      selectedOffer: null,
      pagination: {
        page: 1
      },
      pastOffers: [],
      loadingPastOffers: false
    };
  },
  computed: {
    offers() {
      return this.$store.getters.offers;
    },
    loadingOffers() {
      return this.$store.getters.loadingOffers;
    }
  },
  created() {
    try {
      this.$store.dispatch(LOAD_OFFERS);
      this.fetchPastOffers();
    } catch (error) {
      Event.$emit("serverError");
      console.error(error);
    }
  },
  methods: {
    fetchPastOffers(p = 1) {
      this.loadingPastOffers = true;
      axios.get(`/carry-freight/offers?past&page=${p}`).then((res) => {
        this.pastOffers = res.data.data;

        this.pagination.current_page = res.data.meta ? res.data.meta.current_page : p;
        this.pagination.last_page = res.data.meta.last_page;
        this.pagination.total = res.data.meta.total;
        this.pagination.page = p;

        this.loadingPastOffers = false;
      });
    },
    onAcceptOfferClicked(offer) {
      this.showAcceptConfirmModal = true;
      this.selectedOffer = offer;
    },
    acceptModalCancelledHandler() {
      this.showAcceptConfirmModal = false;
      this.selectedOffer = null;
    },
    acceptModalConfirmedHandler() {
      this.processingOffer = true;
      axios
        .patch(`/carry-freight/offers/${this.selectedOffer.id}/accept`)
        .then(res => {
          this.showAcceptConfirmModal = false;
          if (res.data.message) {
            this.$swal({
              title: "Oops...",
              text: res.data.message
            });
          } else {
            this.$swal({
              type: "success",
              title: "Offer Accepted"
            });
          }

          this.$store.dispatch(LOAD_OFFERS);
          this.fetchPastOffers(1);
        })
        .catch(error => {
          Event.$emit("serverError");
          console.error(error);
        })
        .finally(() => {
          this.showAcceptConfirmModal = false;
          this.processingOffer = false;
          this.selectedOffer = null;
        });
    },

    onDeclineOfferClicked(offer) {
      this.showDeclineConfirmModal = true;
      this.selectedOffer = offer;
    },

    declineModalCancelledHandler() {
      this.showDeclineConfirmModal = false;
      this.selectedOffer = null;
    },

    declineModalConfirmedHandler() {
      this.processingOffer = true;
      axios
        .patch(`/carry-freight/offers/${this.selectedOffer.id}/decline`)
        .then(() => {
          this.showDeclineConfirmModal = false;
          window.location.href = `/offer/${
            this.selectedOffer.id
          }/declined-reason`;
        })
        .catch(error => {
          Event.$emit("serverError");
          console.error(error);
        })
        .finally(() => {
          this.showDeclineConfirmModal = false;
          this.processingOffer = false;
          this.selectedOffer = null;
        });
    }
  }
};
</script>

<style scoped></style>
